/**
 * Transforms the Belle Journée API response to match the frontend menu schema
 */

// Map location names to store IDs
export const STORE_ID_MAP = {
  'Princeton': 1,
  'Fort Lee': 2,
  'Palisades Park': 3
};

// Reverse map for store ID to location name
export const LOCATION_MAP = {
  1: 'Princeton',
  2: 'Fort Lee',
  3: 'Palisades Park'
};

/**
 * Transform API response to frontend menu format
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} - Transformed menu data { menuItems, categories }
 */
export const transformMenuData = (apiResponse) => {
  if (!apiResponse || apiResponse.status !== 'success') {
    throw new Error('Invalid API response');
  }

  const { data } = apiResponse;

  if (!data || !data.categories) {
    throw new Error('No menu data available');
  }

  // Extract all items from all categories and flatten them
  const menuItems = [];
  const categoryNames = ['All']; // Start with 'All' filter

  data.categories.forEach((category) => {
    // Add category name to list
    categoryNames.push(category.title);

    // Transform each item in this category
    if (category.items && category.items.length > 0) {
      category.items.forEach((item) => {
        const transformedItem = {
          id: item.id,
          name: item.title,
          description: item.description || '', // Leave blank if no description
          price: parseFloat(item.price),
          tax: parseFloat(item.tax),
          category: category.title,
          image: item.image || '/logo.png', // Fallback to logo.png if no image
          sort: item.sort,
          modifier_groups: item.modifier_groups || []
        };
        menuItems.push(transformedItem);
      });
    }
  });

  // Sort items by their sort order
  menuItems.sort((a, b) => a.sort - b.sort);

  return {
    menuItems,
    categories: categoryNames
  };
};

/**
 * Fetch menu from API for a specific location
 * @param {string} location - Location name (Princeton, Fort Lee, or Palisades Park)
 * @returns {Promise<Object>} - Menu data
 */
export const fetchMenuForLocation = async (location) => {
  const storeId = STORE_ID_MAP[location];

  if (!storeId) {
    throw new Error(`Invalid location: ${location}`);
  }

  const apiUrl = `https://online.bellejournee.com/api/index.php?request=getmenu&store_id=${storeId}`;

  try {
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return transformMenuData(data);
  } catch (error) {
    console.error('Error fetching menu:', error);
    throw error;
  }
};

/**
 * Calculate cart totals using the API
 * @param {Array} cartItems - Array of cart items with id and quantity
 * @param {string} location - Location name (Princeton, Fort Lee, or Palisades Park)
 * @returns {Promise<Object>} - Cart calculation response
 */
export const calculateCart = async (cartItems, location) => {
  const storeId = STORE_ID_MAP[location];

  if (!storeId) {
    throw new Error(`Invalid location: ${location}`);
  }

  // Transform cart items to API format: [{ item: id, qty: quantity }]
  const cart = cartItems.map(item => ({
    item: item.id,
    qty: item.quantity
  }));

  const formData = new FormData();
  formData.append('store_id', storeId);
  formData.append('cart', JSON.stringify(cart));

  const apiUrl = 'https://online.bellejournee.com/api/index.php?request=cartcalc';

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error calculating cart:', error);
    throw error;
  }
};
